import { useCallback, useEffect, useRef, useState } from "react"
import type { MarketplaceId } from "@nova-ai/shared"

const BRIDGE_BASE_URL = "http://127.0.0.1:5390"

export type CampaignActionBridgeDispatch = {
  planId: string
  phase: "prepare" | "execute"
}

type CampaignActionBridgeError = {
  kind: "connection" | "action"
  message: string
}

export function useCampaignActionBridge(marketplace: MarketplaceId, enabled: boolean) {
  const connectionGeneration = useRef(0)
  const [ready, setReady] = useState(false)
  const [connecting, setConnecting] = useState(false)
  const [error, setError] = useState<CampaignActionBridgeError | null>(null)
  const [planDispatch, setPlanDispatch] = useState<CampaignActionBridgeDispatch | null>(null)
  useEffect(() => {
    connectionGeneration.current += 1
    setReady(false)
    setConnecting(false)
    setError(null)
    setPlanDispatch(null)
  }, [enabled, marketplace])
  const connect = useCallback(async (): Promise<boolean> => {
    if (!enabled) {
      return false
    }
    const requestGeneration = connectionGeneration.current + 1
    connectionGeneration.current = requestGeneration
    setConnecting(true)
    setError(null)
    const available = await campaignActionBridgeReady(marketplace)
    if (connectionGeneration.current !== requestGeneration) {
      return false
    }
    setReady(available)
    if (!available) {
      setError({
        kind: "connection",
        message: "The local campaign runner is unavailable on this computer.",
      })
    }
    setConnecting(false)
    return available
  }, [enabled, marketplace])
  const dispatch = useCallback(async (planId: string, phase: "prepare" | "execute") => {
    setError(null)
    setPlanDispatch({ planId, phase })
    try {
      const response = await fetch(`${BRIDGE_BASE_URL}/v1/plans/${planId}/${phase}`, {
        method: "POST",
        headers: { "content-type": "application/json" },
      })
      if (!response.ok) {
        const body = (await response.json().catch(() => null)) as { error?: unknown } | null
        setReady(true)
        setPlanDispatch(null)
        setError({
          kind: "action",
          message:
            typeof body?.error === "string" ? body.error : `bridge returned ${response.status}`,
        })
        return
      }
      setReady(true)
    } catch (cause) {
      setReady(false)
      setPlanDispatch(null)
      setError({
        kind: "connection",
        message: cause instanceof Error ? cause.message : String(cause),
      })
    }
  }, [])
  return { ready, connecting, error: error?.message ?? null, planDispatch, connect, dispatch }
}

async function campaignActionBridgeReady(marketplace: MarketplaceId): Promise<boolean> {
  const signal = AbortSignal.timeout(750)
  try {
    const response = await fetch(`${BRIDGE_BASE_URL}/v1/status`, { signal })
    if (!response.ok) {
      return false
    }
    const body = (await response.json()) as { ok?: unknown; marketplace?: unknown }
    return body.ok === true && body.marketplace === marketplace
  } catch {
    return false
  }
}
